import React, { useMemo, useState } from 'react';
import { Form, Input, Button, Typography, Select, DatePicker, InputNumber, Row, Col, Card, Grid, Alert, Spin } from 'antd';
import { UserOutlined, SearchOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { EmployerDTO, SelfRegistrationSessionDTO, SelfRegistrationStepRequestDTO } from '@/shared/api/model';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface PersonalInfoStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const [form] = Form.useForm();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [employerOptions, setEmployerOptions] = useState<EmployerDTO[]>([]);
    const [isSearchingEmployers, setIsSearchingEmployers] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [employerMode, setEmployerMode] = useState<'search' | 'manual'>(
        session?.employerId ? 'search' : (session?.employerName && !session?.employerId ? 'manual' : 'search')
    );

    // Use the refactored self registration hook
    const {
        updatePersonalInfo,
        isUpdatingPersonalInfo
    } = useSelfRegistration();

    const employerSelectOptions = useMemo(() => {
        return employerOptions.map((e) => ({
            value: e.id as number,
            label: `${e.name ?? ''}${e.voteNumber ? ` (${e.voteNumber})` : ''}`.trim()
        }));
    }, [employerOptions]);

    const normalizeFullName = (value?: string) => {
        return (value || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((part) => part.toUpperCase())
            .join(' ');
    };

    const searchEmployers = async (q: string) => {
        const query = (q ?? '').trim();

        if (!query) {
            setEmployerOptions([]);
            return;
        }

        setIsSearchingEmployers(true);
        try {
            const resp = await AXIOS_INSTANCE.get('/frontend/v1/employers/public-search', {
                params: { q: query, limit: 20 }
            });
            const items = (resp?.data?.data || []) as EmployerDTO[];
            setEmployerOptions(items);
        } catch (e) {
            setEmployerOptions([]);
        } finally {
            setIsSearchingEmployers(false);
        }
    };

    const handleUpdatePersonalInfo = async (values: any) => {
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }

            // Format the data for API
            const normalizedFullName = normalizeFullName(values.fullName);

            const personalInfoData: SelfRegistrationStepRequestDTO = {
                sessionToken: session.sessionToken!,
                fullName: normalizedFullName,
                dateOfBirth: values.dateOfBirth?.format('YYYY-MM-DD'),
                gender: values.gender,
                nationalId: values.nationalId,
                employeeId: values.employeeId,
                voteNumber: values.voteNumber,
                governmentEmployee: values.governmentEmployee,
                grossSalary: values.grossSalary,
                netSalary: values.netSalary,
                employerId: employerMode === 'search' && values.employerId ? values.employerId : undefined,
                employerName: employerMode === 'search' && values.employerId ? values.employerName : values.customEmployerName,
            };

            form.setFieldsValue({ fullName: normalizedFullName });

            console.log('🚀 Submitting personal info:', personalInfoData);
            
            // ✅ Await the API call and handle the response
            const updatedSession = await updatePersonalInfo(personalInfoData);
            
            console.log('✅ Personal info updated, next step:', updatedSession.currentStep);

            // Show a brief loader before navigating to give background processes time to settle
            setIsNavigating(true);
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // ✅ Trigger progression to next step
            onComplete(updatedSession);
            
        } catch (error: any) {
            console.error('❌ Personal info update failed:', error);
            setIsNavigating(false);
            onError(error.message || 'Failed to update personal information');
        }
    };

    // Initialize form with existing session data
    React.useEffect(() => {
        if (session) {
            form.setFieldsValue({
                fullName: normalizeFullName(session.fullName),
                dateOfBirth: session.dateOfBirth ? moment(session.dateOfBirth) : null,
                gender: session.gender,
                nationalId: session.nationalId,
                employeeId: session.employeeId,
                voteNumber: session.voteNumber,
                governmentEmployee: session.governmentEmployee,
                grossSalary: session.grossSalary,
                netSalary: session.netSalary,
                employerId: session.employerId,
                employerName: session.employerName,
                customEmployerName: session.employerId ? undefined : session.employerName,
            });
        }
    }, [session, form]);

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <UserOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#1890ff', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Personal Information</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Please provide your personal and employment details
                </Text>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleUpdatePersonalInfo}
                size={isMobile ? 'middle' : 'large'}
                scrollToFirstError
            >
                {/* Personal Information Section */}
                <Card 
                    title="Personal Details" 
                    style={{ marginBottom: isMobile ? '16px' : '20px' }}
                    size={isMobile ? 'small' : 'default'}
                >
                    <Row gutter={isMobile ? [0, 16] : [16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="fullName"
                                label="Full Name"
                                tooltip={"As it appears on your salary slip.\n Example as John John Mdoe"}
                                rules={[
                                    { required: true, message: 'Please enter your full name' },
                                    { min: 2, message: 'Name must be at least 2 characters' }
                                ]}
                            >
                                <Input
                                    placeholder="Enter your full name"
                                    onBlur={(event) => {
                                        const normalized = normalizeFullName(event.target.value);
                                        form.setFieldsValue({ fullName: normalized });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="dateOfBirth"
                                label="Date of Birth"
                                rules={[
                                    { required: true, message: 'Please select your date of birth' }
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="Select date of birth"
                                    disabledDate={(current) => current && current.isAfter(dayjs().subtract(18, 'years'), 'day')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={isMobile ? [0, 16] : [16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="gender"
                                label="Gender"
                                rules={[
                                    { required: true, message: 'Please select your gender' }
                                ]}
                            >
                                <Select placeholder="Select gender">
                                    <Option value="MALE">Male</Option>
                                    <Option value="FEMALE">Female</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="nationalId"
                                label="National ID"
                                dependencies={['dateOfBirth']}
                                rules={[
                                    { required: true, message: 'Please enter your national ID' },
                                    { pattern: /^[0-9]{20}$/, message: 'National ID must be exactly 20 digits' },
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            if (!/^[0-9]{20}$/.test(value)) {
                                                return Promise.resolve();
                                            }
                                            const dob = form.getFieldValue('dateOfBirth');
                                            if (!dob) {
                                                return Promise.resolve();
                                            }
                                            const expectedPrefix = dob.format('YYYYMMDD');
                                            if (!value.startsWith(expectedPrefix)) {
                                                return Promise.reject(new Error(`National ID must start with your birth date (${expectedPrefix})`));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <Input placeholder="Enter your national ID number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={isMobile ? [0, 16] : [16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="employeeId"
                                label="Employee ID / Check Number"
                                rules={[
                                    { required: true, message: 'Please enter your employee ID or check number' },
                                    { min: 2, message: 'Employee ID must be at least 2 characters' }
                                ]}
                            >
                                <Input placeholder="Enter your employee ID or check number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="voteNumber"
                                label="Vote Number"
                                rules={[
                                    { required: true, message: 'Please enter your vote number' },
                                    { min: 2, message: 'Vote number must be at least 2 characters' }
                                ]}
                            >
                                <Input placeholder="Enter your vote number" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Employment Information Section */}
                <Card title="Employment Details" style={{ marginBottom: '20px' }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="governmentEmployee"
                                label="Employment Type"
                                rules={[
                                    { required: true, message: 'Please select employment type' }
                                ]}
                            >
                                <Select placeholder="Select employment type">
                                    <Option value={true}>Government Employee</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                <Button
                                    type={employerMode === 'search' ? 'primary' : 'default'}
                                    size="small"
                                    icon={<SearchOutlined />}
                                    onClick={() => {
                                        setEmployerMode('search');
                                        form.setFieldsValue({ customEmployerName: undefined });
                                    }}
                                    style={{ marginRight: 8 }}
                                >
                                    Search Employer
                                </Button>
                                <Button
                                    type={employerMode === 'manual' ? 'primary' : 'default'}
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setEmployerMode('manual');
                                        form.setFieldsValue({ employerId: undefined, employerName: undefined });
                                    }}
                                >
                                    Type Employer Name
                                </Button>
                            </div>

                            {employerMode === 'search' ? (
                                <>
                                    <Form.Item
                                        name="employerId"
                                        label="Employer Name"
                                        rules={[
                                            { required: true, message: 'Please select your employer or switch to manual entry' }
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Search and select employer"
                                            filterOption={false}
                                            onSearch={searchEmployers}
                                            options={employerSelectOptions}
                                            loading={isSearchingEmployers}
                                            allowClear
                                            onChange={(id) => {
                                                if (!id) {
                                                    form.setFieldsValue({ employerName: undefined, voteNumber: undefined });
                                                    return;
                                                }
                                                const selected = employerOptions.find((e) => e.id === id);
                                                if (!selected) {
                                                    form.setFieldsValue({ employerName: undefined, voteNumber: undefined });
                                                    return;
                                                }
                                                form.setFieldsValue({
                                                    employerName: selected.name,
                                                    voteNumber: selected.voteNumber
                                                });
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item name="employerName" hidden>
                                        <Input />
                                    </Form.Item>
                                </>
                            ) : (
                                <>
                                    <Form.Item
                                        name="customEmployerName"
                                        label="Employer Name"
                                        rules={[
                                            { required: true, message: 'Please enter your employer name' },
                                            { min: 2, message: 'Employer name must be at least 2 characters' }
                                        ]}
                                    >
                                        <Input placeholder="Type your employer name" />
                                    </Form.Item>
                                    <Alert
                                        message="Your employer will be verified during review"
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                </>
                            )}
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="grossSalary"
                                label="Gross Salary (TZS)"
                                rules={[
                                    { required: true, message: 'Please enter your gross salary' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter gross salary"
                                    min={0}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    // @ts-ignore
                                    parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) || 0 : 0}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="netSalary"
                                label="Net Salary (TZS)"
                                rules={[
                                    { required: true, message: 'Please enter your net salary' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter net salary"
                                    min={0}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    // @ts-ignore
                                    parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) || 0 : 0}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Location Information will be handled in separate LocationSelectionStep */}

                <Form.Item style={{ marginTop: isMobile ? '20px' : '24px' }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isUpdatingPersonalInfo || isNavigating}
                        disabled={isUpdatingPersonalInfo || isNavigating}
                        block
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                        icon={isNavigating ? <LoadingOutlined /> : undefined}
                    >
                        {isNavigating ? 'Processing...' : 'Continue to Location Information'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default PersonalInfoStep;
